import { NewApiBaseUrl } from '../consts/new-api-base-url'

/**
 * @typedef {Object} CsvManageableOrganizationDto
 * @property {number} id
 * @property {string} shortDescription
 * @property {string} description
 */

/**
 * @typedef {Object} CsvTemplateDto
 * @property {number} basicOrganization
 * @property {string} basicOrganizationName
 * @property {string} filename
 * @property {CsvManageableOrganizationDto[]} manageableOrganizations
 */

/**
 * @typedef {Object} CsvTemplatesResponseDto
 * @property {CsvTemplateDto[]} templates
 */

/**
 * @typedef {Object} CsvImportSummaryDto
 * @property {number} rowsTotal
 * @property {number} groupsTotal
 * @property {number} imported
 * @property {number} duplicatesSkipped
 * @property {number} errors
 */

/**
 * @typedef {Object} CsvImportDetailDto
 * @property {string} crashExternalKey
 * @property {'imported'|'duplicate_skipped'|'error'} status
 * @property {string} [crashId]
 * @property {string} [reason]
 * @property {string} [message]
 * @property {string} [reportDateHour]
 * @property {string} [roadNameNorm]
 * @property {string} [csvCrashIdNorm]
 * @property {string} [existingCrashId]
 */

/**
 * @typedef {Object} CsvImportResponseDto
 * @property {string} batchId
 * @property {CsvImportSummaryDto} summary
 * @property {CsvImportDetailDto[]} details
 */

const getApiRoot = () => String(NewApiBaseUrl).replace(/\/$/, '')

const getCrashCsvBaseUrl = () => `${getApiRoot()}/CrashCsv`

/**
 * Supporta risposta piatta (documento API) o envelope `responseData` usato altrove nell'app.
 * @param {unknown} data
 * @returns {Record<string, unknown>|null}
 */
export function unwrapApiPayload(data) {
  if (data == null || typeof data !== 'object') return null
  const obj = /** @type {Record<string, unknown>} */ (data)
  if ('responseData' in obj && obj.responseData != null && typeof obj.responseData === 'object') {
    return /** @type {Record<string, unknown>} */ (obj.responseData)
  }
  return obj
}

/**
 * @param {unknown} data
 * @returns {CsvTemplatesResponseDto|null}
 */
export function parseTemplatesResponse(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || !Array.isArray(inner.templates)) return null
  return /** @type {CsvTemplatesResponseDto} */ (/** @type {unknown} */ (inner))
}

/**
 * @param {unknown} data
 * @returns {CsvImportResponseDto|null}
 */
export function parseImportResponse(data) {
  const inner = unwrapApiPayload(data)
  if (!inner || typeof inner.batchId !== 'string' || !inner.summary || !Array.isArray(inner.details)) {
    return null
  }
  return /** @type {CsvImportResponseDto} */ (/** @type {unknown} */ (inner))
}

/**
 * @param {string} bearerToken - senza prefisso "Bearer "
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function getCrashCsvTemplates(bearerToken) {
  const url = `${getCrashCsvBaseUrl()}/templates`
  const res = await fetch(url, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${bearerToken}`,
      Accept: 'application/json'
    }
  })
  const text = await res.text()
  let body = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = { raw: text }
    }
  }
  return { status: res.status, body }
}

/**
 * Estrae filename da Content-Disposition attachment; fallback se assente.
 * @param {string | null} header
 * @param {number} basicOrganization
 */
export function parseContentDispositionFilename(header, basicOrganization) {
  if (!header) return `crash-csv-template-package-${basicOrganization}.zip`
  const m = /filename\*?=(?:UTF-8''|")?([^";\n]+)"?/i.exec(header)
  if (m && m[1]) {
    try {
      return decodeURIComponent(m[1].trim())
    } catch {
      return m[1].trim()
    }
  }
  return `crash-csv-template-package-${basicOrganization}.zip`
}

/**
 * @param {number} basicOrganization
 * @param {string} bearerToken
 * @returns {Promise<{ status: number, blob: Blob, filename: string } | { status: number, error: string }>}
 */
export async function downloadCrashCsvTemplate(basicOrganization, bearerToken) {
  const url = new URL(`${getCrashCsvBaseUrl()}/template`)
  url.searchParams.set('basicOrganization', String(basicOrganization))

  const res = await fetch(url.toString(), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${bearerToken}`
    }
  })

  if (!res.ok) {
    const text = await res.text()
    let message = text || res.statusText
    try {
      const j = JSON.parse(text)
      if (j && typeof j === 'object' && 'message' in j && typeof j.message === 'string') {
        message = j.message
      }
    } catch {
      /* keep text */
    }
    return { status: res.status, error: message }
  }

  const blob = await res.blob()
  const cd = res.headers.get('Content-Disposition')
  const filename = parseContentDispositionFilename(cd, basicOrganization)
  return { status: res.status, blob, filename }
}

/**
 * POST multipart: non impostare Content-Type (boundary da browser).
 *
 * @param {File} csvFile
 * @param {number} organizationId
 * @param {string} bearerToken
 * @returns {Promise<{ status: number, body: unknown }>}
 */
export async function postCrashCsvImport(csvFile, organizationId, bearerToken) {
  const form = new FormData()
  form.append('csvFile', csvFile, csvFile.name || 'import.csv')
  form.append('organizationId', String(organizationId))

  const res = await fetch(`${getCrashCsvBaseUrl()}/import`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${bearerToken}`
    },
    body: form
  })

  const text = await res.text()
  let body = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = { raw: text }
    }
  }
  return { status: res.status, body }
}
